import React, { useRef, useState, useContext, createContext, useCallback } from "react";
import FloatLabel from "../float-label/Index";
import { useFloating, autoUpdate, size, flip, offset } from "@floating-ui/react-dom";
import "../select/index.css";
import { useEffect } from "react";
import { nanoid } from "nanoid";
import { Checkbox, Button, Tooltip } from "antd";
import { CloseCircleOutlined, DownOutlined, UpOutlined, SearchOutlined } from "@ant-design/icons";
import { Virtuoso } from 'react-virtuoso'
import { createPortal } from "react-dom";

const SelectContext = createContext({});

export const Option = ({ dropdownId, index, label, value, color, isSelected }) => {
  const { listRef, activeIndex, handleClickOption, allOptionSelected, allOptionIndeterminate } = useContext(SelectContext);

  return (
    <div
      tabIndex={-1}
      id={value}
      className={`${dropdownId} Option ${activeIndex === index ? "OptionActive" : ""}`}
      role="option"
      ref={(node) => (listRef.current[index] = node)}
      onClick={() => handleClickOption(value)}
      style={{
        display: "flex",
        justifyContent: "flex-start",
        background: isSelected ? "#9df4ff50" : undefined,
      }}
    >
      <Checkbox
        style={{ marginRight: 12, pointerEvents: "none" }}
        checked={value === null ? allOptionSelected : isSelected}
        indeterminate={value === null ? allOptionIndeterminate : null}
        tabIndex={-1}
      />
      <span style={{ color }}>{label}</span>
    </div>
  );
};

const specialKeysToIgnoreForOpeningDropdown = {
  "Enter": true,
  "Tab": true,
  "0": true,
  "Ctrl": true,
  "Control": true,
  "Shift": true,
  "Alt": true,
  "F2": true,
  "F3": true,
  "F4": true,
  "ArrowLeft": true,
  "ArrowRight": true,
  "PageUp": true,
  "PageDown": true,
  "ArrowUp": true,
}

const AllSingleMultiSelect = ({
  id,
  bordered = true,
  openMenuOnFocus = false,
  options = [],
  onChange,
  value = { mode: "all", options: [] },
  uppercase = true,
  onKeyDown,
  onBlur,
  readOnly,
  skipFocus,
  tabIndex,
  placeholder,
  disabled,
  required,
  forwardedRef,
  focusedOptionDetailsRender,
  showSearch,
  filterFunc,
  allowClear = true,
  autoComplete,
  autoFocus,
  style,
  dropdownMinWidth,
}) => {
  const dropdownIdRef = useRef(nanoid());
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const inputRef = forwardedRef || useRef();
  const virtuosoRef = useRef();
  const selectedValuesRef = useRef(value?.options || []);
  const selectedValuesMapRef = useRef({});
  const selectedValuesMap = value?.options?.reduce?.((acc, curr) => {
    acc[curr] = true;
    return acc;
  }, {}) || {};
  const selectedOptions = options.filter(option => selectedValuesMap[option.value]);
  const selectedValuesText = options.length && selectedOptions.length === options.length ? "ALL" : selectedOptions
    .map(option => option.label)
    .join(", ");
  const closeDropdown = (e) => {
    setOpen(false);
    setActiveIndex(-1);
    setSearchValue("");
    if (e?.key !== "Tab") {
      inputRef?.current?.focus?.();
    }
  }

  useEffect(() => {
    selectedValuesRef.current = value?.options || [];
    selectedValuesMapRef.current = selectedValuesRef.current.reduce?.((acc, curr) => {
      acc[curr] = true;
      return acc;
    }, {}) || {};
  }, [value]);

  const [activeIndex, setActiveIndex] = useState(-1);
  const upperCasedSearchValue = searchValue?.toUpperCase();
  const filteredOptions = searchValue
    ? options
      .filter(filterFunc ? (obj) => filterFunc(obj, upperCasedSearchValue) : (obj) => obj.label.toUpperCase().includes(upperCasedSearchValue))
      .sort((a, b) => {
        const labelA = a.label.toUpperCase();
        const labelB = b.label.toUpperCase();

        if (
          labelA.startsWith(upperCasedSearchValue) !== labelB.startsWith(upperCasedSearchValue)
        ) {
          if (
            labelA.startsWith(upperCasedSearchValue) &&
            !labelB.startsWith(upperCasedSearchValue)
          ) {
            return -1;
          }
          return 1;
        }
        return labelA < labelB ? -1 : 1;
      })
    : [{ label: "ALL [ CTRL + A ]", value: null, }, ...options];

  const listItemsRef = useRef([]);

  useEffect(() => {
    function clickListener(e) {
      const dropdownElements = Array.from(document.getElementsByClassName(dropdownIdRef.current));
      const isFocusedElementInsideDropdown = dropdownElements.some(element => element.contains(e.target));
      if (!isFocusedElementInsideDropdown) {
        closeDropdown();
      }
    }
    if (open) {
      window.addEventListener("focusin", clickListener);
    }
    return () => {
      window.removeEventListener("focusin", clickListener);
    };
  }, [open]);

  const { x, y, refs, strategy } = useFloating({
    open,
    onOpenChange: setOpen,
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({ padding: 8 }),
      size({
        apply({ rects, elements }) {
          if (rects.reference.width) {
            Object.assign(elements.floating.style, {
              width: `${rects.reference.width}px`,
            });
          }
        },
      }),
    ],
  });

  function handleFocus() {
    if (openMenuOnFocus) {
      setOpen(true);
    }
  }

  useEffect(() => {
    if (open) {
      virtuosoRef.current?.scrollIntoView({ index: activeIndex });
    }
  }, [open, activeIndex]);

  const toggleSelection = (optionValue) => {
    if (onChange) {
      if (optionValue === null) { // null value is for the All option
        const filteredValuesMap = filteredOptions.reduce((acc, curr) => {
          acc[curr.value] = true;
          return acc;
        }, {});
        const selectedFilteredValues = selectedValuesRef.current.filter(value => filteredValuesMap[value]);
        const selectedFilteredValuesMap = selectedFilteredValues.reduce((acc, curr) => {
          acc[curr] = true;
          return acc;
        }, {});
        if (selectedFilteredValues?.length) {
          const newSelectedValues = selectedValuesRef.current.filter(value => !selectedFilteredValuesMap[value]);
          onChange({ mode: newSelectedValues.length === 0 ? "all" : "multiple", options: newSelectedValues });
        } else {
          const newSelectedValues = selectedValuesRef.current.slice();
          filteredOptions.forEach(option => {
            if (option.value !== null && !selectedFilteredValuesMap[option.value]) {
              newSelectedValues.push(option.value)
            }
          })
          onChange({ mode: newSelectedValues.length === options.length ? "all" : "multiple", options: newSelectedValues });
        }
      } else {
        if (!selectedValuesMapRef.current[optionValue]) {
          const newSelectedValues = selectedValuesRef.current.slice();
          newSelectedValues.push(optionValue);
          onChange({
            mode: newSelectedValues.length === 1 ? "single" : newSelectedValues.length === options.length ? "all" : "multiple",
            options: newSelectedValues
          });
        } else {
          const newSelectedValues = selectedValuesRef.current.filter(value => value !== optionValue);
          onChange({
            mode: newSelectedValues.length === 0 ? "all" : newSelectedValues.length === 1 ? "single" : "multiple",
            options: newSelectedValues,
          });
        }
      }
    }
  };

  const handleInputKeyDown = async (e) => {
    const key = e.key;

    if (key?.toUpperCase() === "C" && (e.ctrlKey || e.metaKey)) {
      navigator.clipboard.writeText(String(filteredOptions[activeIndex]?.label));
    }
    switch (key) {
      case "Tab": {
        closeDropdown(e);
        break;
      }
      case "Enter": {
        if (open) {
          if (activeIndex > -1) {
            e.preventDefault();
            e.stopPropagation();
            toggleSelection(filteredOptions[activeIndex]?.value);
          } else {
            closeDropdown(e);
          }
        }
        break;
      }
      case "A":
      case "a": {
        if (e.ctrlKey) {
          handleClickOption(null);
        }
        break;
      }
      case "Delete": {
        e.stopPropagation();
        setSearchValue("");
        break;
      }
      case "Backspace": {
        e.stopPropagation();
        break;
      }
      case "Escape": {
        e.preventDefault();
        e.stopPropagation();
        closeDropdown(e);
        inputRef.current?.focus();
        break;
      }
      case "ArrowDown": {
        if (open && (activeIndex < filteredOptions.length - 1)) {
          setActiveIndex(activeIndex + 1);
        }
        break;
      }
      case "ArrowUp": {
        if (activeIndex >= 0) {
          e.preventDefault();
          e.stopPropagation();
          setActiveIndex(activeIndex - 1);
        }
        break;
      }
    }

    if (!open && !specialKeysToIgnoreForOpeningDropdown[key] && !e.ctrlKey) {
      setOpen(true);
    }

    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  const handleChangeInput = (e) => {
    setSearchValue(uppercase ? e.target.value.toUpperCase() : e.target.value);
    if (e.target.value) {
      setActiveIndex(0);
    } else {
      setActiveIndex(-1);
    }
  };

  const handleClickOption = (clickedOptionValue) => {
    setActiveIndex(filteredOptions.findIndex(option => option.value === clickedOptionValue));
    toggleSelection(clickedOptionValue);
    inputRef.current?.focus?.();
    const searchTextLength = searchValue?.length;
    setTimeout(() => inputRef.current?.setSelectionRange?.(searchTextLength, searchTextLength));
  };

  const handleClickInput = () => {
    if (!open) {
      setOpen(true);
    }
  };

  const handleClickClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange({ mode: "all", options: [] });
  };

  const showClearIcon = allowClear && hover && value?.options?.length > 0;

  let rightIcon = null;
  if (!showClearIcon) {
    if (!open && hover) {
      rightIcon = <SearchOutlined style={{ color: "#bbb", fontSize: 12 }} />;
    } else if (open) {
      rightIcon = <UpOutlined style={{ color: "#bbb", fontSize: 12 }} onClick={(e) => { e.stopPropagation(); closeDropdown(e); }} />
    } else {
      rightIcon = <DownOutlined style={{ color: "#bbb", fontSize: 12 }} onClick={(e) => { e.stopPropagation(); setOpen(true) }} />;
    }
  }

  const virtuosoHeight = Math.min(filteredOptions.length * 34, 300);
  return (
    <FloatLabel label={placeholder} required={required}>
      <SelectContext.Provider
        value={{
          activeIndex,
          listRef: listItemsRef,
          handleClickOption,
          selectedValues: value?.options || [],
          allOptionSelected: !value?.options?.length ? false : options?.length && options.length === value?.options?.length ? true : null,
          allOptionIndeterminate: options?.length && value?.options?.length && value?.options?.length < options.length,
        }}
      >
        <div
          className={`Select ${dropdownIdRef.current} ${showSearch ? "SelectSearch" : ""}
          ${bordered ? " SelectBordered" : ""}
          ${readOnly ? " SelectReadOnly" : ""}`}
          style={style}
          ref={refs.setReference}
          onClick={() => {
            inputRef.current?.focus();
            setOpen(true);
          }}
          onMouseEnter={allowClear ? () => setHover(true) : null}
          onMouseLeave={allowClear ? () => setHover(false) : null}
        >
          {!open && (
            <Tooltip title={selectedValuesText} placement="top" trigger="hover">
              <p style={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                margin: 0
              }}>{selectedValuesText}</p>
            </Tooltip>
          )}
          <input
            id={id}
            className={`select ant-input custom-input ant-input-borderless ${!showSearch ? " hide-cursor" : ""}`}
            placeholder={open ? (selectedValuesText || "Search option") : ""}
            disabled={disabled}
            tabIndex={readOnly || skipFocus ? -1 : tabIndex}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            value={open ? searchValue : ""}
            readOnly={readOnly}
            onChange={!readOnly ? handleChangeInput : null}
            ref={inputRef}
            onFocus={handleFocus}
            onKeyDown={handleInputKeyDown}
            onClick={handleClickInput}
            onBlur={() => {
              if (onBlur) {
                onBlur();
              }
            }}
          />
          {showClearIcon && (
            <CloseCircleOutlined
              onClick={handleClickClear}
              style={{
                color: "#999",
                fontSize: 12,
                right: 8,
                cursor: "pointer",
                zIndex: 2
              }}
            />
          )}
          {rightIcon}
        </div>
        {!disabled && !readOnly && open && (
          <>
            {createPortal(
              <div
                className={`SelectDropDown ${dropdownIdRef?.current}`}
                tabIndex={-1}
                ref={refs.setFloating}
                style={{
                  position: strategy,
                  top: y ?? "",
                  left: x ?? "",
                  minWidth: dropdownMinWidth
                }}
              >
                <div className="SelectOptionsContainer">
                  {filteredOptions.length > 0 ? (
                    <Virtuoso
                      ref={virtuosoRef}
                      style={{ height: virtuosoHeight }}
                      totalCount={filteredOptions.length}
                      data={filteredOptions}
                      initialTopMostItemIndex={0}
                      itemContent={(index, data) => <Option
                        key={data?.value}
                        value={data?.value}
                        label={data?.label}
                        index={index}
                        dropdownId={dropdownIdRef.current}
                        isSelected={selectedValuesMapRef.current[data?.value]}
                        color={data?.color}
                      />}
                    />
                  ) : (
                    (
                      <div
                        style={{
                          padding: 16,
                          height: 150,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span style={{ color: "grey" }}>No Data...</span>
                      </div>
                    )
                  )}
                </div>
                {focusedOptionDetailsRender && (
                  <>
                    <div style={{ borderTop: "1px solid lightgrey" }} />
                    {focusedOptionDetailsRender(filteredOptions[activeIndex]?.value)}
                  </>
                )}
                <div style={{ padding: 8, display: "flex", justifyContent: "center", borderTop: "1px solid lightgrey" }}>
                  <Button
                    size="small"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      closeDropdown(e);
                    }}
                  >
                    Close (Escape)
                  </Button>
                </div>
              </div>,
              document.body
            )}
          </>
        )}
      </SelectContext.Provider>
    </FloatLabel >
  );
};

export default AllSingleMultiSelect;
